import { enumObject, EnumValue } from '@/utils';
import { CategoryLookupDto } from '../http.categories';
import { ItemMenuItemType } from '../http.itemMenus';
import { ItemDto, ItemPricePer } from '../http.items/itemTypes';
import {
  ProjectStatus,
  ProjectStepLookupDto,
  ProjectType,
} from '../http.projects';
import { RoomLookupDto } from '../http.rooms';
import { SalesTaxType } from '../http.subscriptions';
import { UserLookupDto } from '../http.users';

export interface ManualPriceDetailsDto {
  isBaseCostOverridden: boolean;
  isOwnerPriceOverridden: boolean;
  isResidentPriceOverridden: boolean;
}

export interface GeoItemPosition {
  x: number;
  y: number;
}

export interface PersonalizationSubOptionDto {
  id: string;
  name: string;
}

export interface PersonalizationItemPricingDataDto {
  pricePer?: ItemPricePer;
  type?: ItemMenuItemType;
  squareFootage?: number;
  baseCost?: number;
  ownerPrice?: number;
  residentPrice?: number;
  totalBaseCost?: number;
  totalOwnerPrice?: number;
  totalResidentPrice?: number;
  residentTaxPrice?: number;
  residentTax?: number;
  residentPriceWithoutTax?: number;
  residentPriceOverridenAt?: string;
  residentPriceOverridenBy?: UserLookupDto;
  tbd: boolean;
  notInMenu: boolean;
}

export interface AvailableItemDto extends ItemDto {
  pricing?: PersonalizationItemPricingDataDto;
}

export type PersonalizationItemDto = Pick<
  ItemDto,
  'name' | 'description' | 'pricePer' | 'communityId'
> & {
  id: string;
  homeId: string;
  communityId: string;
  projectId: string;
  itemId: string;
  room?: RoomLookupDto;
  step: ProjectStepLookupDto;
  category: PersonalizationItemCategoryDto;
  position?: GeoItemPosition;
  suboptionId?: string;
  notes?: string;
  isFloorplan: boolean;
  subOption?: PersonalizationSubOptionDto;
  number?: number;
  pricing: PersonalizationItemPricingDataDto | null;
  hexColor: string;
  floorplanRoomId: string | null;
  floorplanRoom?: RoomLookupDto;
};

export interface AddPersonalizationItemDto {
  homeId: string;
  projectId: string;
  notes?: string;
  itemId: string;
  roomId?: string;
  suboptionId?: string;
  stepId: string;
  position?: GeoItemPosition;
  isFloorplan: boolean;
  categoryId?: string;
  floorplanRoomId?: string | null;
}

export interface UpdatePersonalizationItemDto {
  id: string;
  notes?: string;
  suboptionId?: string;
}

export interface PersonalizationItemCategoryDto {
  category: CategoryLookupDto;
  root: CategoryLookupDto;
  path: CategoryLookupDto[];
}

export interface UpdateGeoPersonalizationItemsPositionDto {
  projectId: string;
  homeId: string;
  stepIds: string[];
  items: { id: string; x: number; y: number }[];
  specialRequests: { id: string; x: number; y: number }[];
}

const PRICING_TYPE_VALUES = ['Base', 'Owner', 'Resident'] as const;

export type PricingType = EnumValue<typeof PRICING_TYPE_VALUES>;

// eslint-disable-next-line
export const PricingType = enumObject('PricingType', PRICING_TYPE_VALUES);

export interface SavePersonalizationItemManualPriceDto {
  id: string;
  amount: number | null;
  type: PricingType;
}

export interface PersonalizationHistoryDto {
  projectId: string;
  projectName: string;
  projectType: ProjectType;
  finalizedAt?: string;
  status: ProjectStatus;
  isActive: boolean;
}

export interface AddExistingItemDto {
  projectId: string;
  homeId: string;
  roomId?: string;
  categoryId: string;
  stepId: string;
  name: string;
}

export type ExistingBaseItemDto = AddExistingItemDto & {
  id: string;
};

export type ExistingItemDto = ExistingBaseItemDto & {
  room?: RoomLookupDto;
  step: ProjectStepLookupDto;
  category: PersonalizationItemCategoryDto;
};

export interface UpdateExistingItemDto {
  id: string;
  name: string;
}

export function getItemTotalPriceBySalesTaxType(
  pricing: Pick<
    PersonalizationItemPricingDataDto,
    'totalResidentPrice' | 'residentPriceWithoutTax'
  >,
  salesTaxType: SalesTaxType = 'ItemLevel',
) {
  return salesTaxType === 'ItemLevel'
    ? pricing.totalResidentPrice
    : pricing.residentPriceWithoutTax;
}
